/*
 * <announcement-marquee> — seamless, infinitely scrolling announcement bar.
 *
 * Clones the announcement group until it more than fills the bar, then animates
 * the track by exactly one group width so the loop has no visible seam. Speed is
 * expressed in pixels per second, so short and long announcements scroll at the
 * same pace.
 */
if (!customElements.get('announcement-marquee')) {
  customElements.define(
    'announcement-marquee',
    class AnnouncementMarquee extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-marquee-track]');
        this.group = this.querySelector('[data-marquee-group]');
        if (!this.track || !this.group) return;

        this.speed = parseFloat(this.dataset.speed) || 50;
        this.build = this.build.bind(this);

        this.build();

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(this.build, 150);
          });
          this.resizeObserver.observe(this);
        }

        // Web fonts change the measured width, so re-measure once they land.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.build);
        }
      }

      disconnectedCallback() {
        clearTimeout(this.resizeTimer);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      build() {
        if (!this.isConnected || !this.track || !this.group) return;

        this.track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());

        const groupWidth = this.group.getBoundingClientRect().width;
        if (!groupWidth) return;

        const containerWidth = this.getBoundingClientRect().width || groupWidth;
        // One copy scrolls off-screen while the copies behind it cover the bar.
        const copies = Math.ceil(containerWidth / groupWidth) + 1;

        const fragment = document.createDocumentFragment();
        for (let i = 0; i < copies; i++) {
          const clone = this.group.cloneNode(true);
          clone.setAttribute('data-marquee-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-marquee-group');
          // Duplicated links must not be reachable by keyboard or screen readers.
          clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
          // Only the original blocks should be selectable in the theme editor.
          clone
            .querySelectorAll('[data-shopify-editor-block]')
            .forEach((block) => block.removeAttribute('data-shopify-editor-block'));
          fragment.appendChild(clone);
        }
        this.track.appendChild(fragment);

        this.style.setProperty('--marquee-group-width', `${groupWidth}px`);
        this.style.setProperty('--marquee-duration', `${Math.max(groupWidth / this.speed, 4)}s`);
      }
    }
  );
}

